import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Search, BookOpen, ChevronRight, Loader2, Sparkles, ArrowLeft, MessageCircle,
  Hash, Clock, ExternalLink,
} from "lucide-react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { PipeText } from "../lib/pipeText";

const openSandy = (prompt) => {
  window.dispatchEvent(new CustomEvent("sandy:open", { detail: prompt ? { prompt } : {} }));
};

function AskSandyCTA({ prompt, label = "Ask Sandy" }) {
  return (
    <button
      onClick={() => openSandy(prompt)}
      data-testid="kb-ask-sandy-btn"
      className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-gradient-to-r from-rose-500 to-orange-500 text-white text-sm font-semibold hover:brightness-110 transition"
    >
      <MessageCircle size={16} /> {label}
    </button>
  );
}

function Sidebar({ categories, activeCategory, onSelect, loading }) {
  return (
    <aside className="lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto pr-1">
      <div className="overline text-zinc-500 mb-3">Categories</div>
      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : (
        <ul className="space-y-1" data-testid="kb-sidebar-list">
          <li>
            <button
              onClick={() => onSelect(null)}
              data-testid="kb-cat-all"
              className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition ${
                !activeCategory ? "bg-rose-500/15 text-rose-300 border border-rose-500/30" : "hover:bg-white/5 text-zinc-400"
              }`}
            >
              <span>All articles</span>
              <span className="text-xs text-zinc-500">{categories.reduce((n, c) => n + c.count, 0)}</span>
            </button>
          </li>
          {categories.map((c) => (
            <li key={c.category}>
              <button
                onClick={() => onSelect(c.category)}
                data-testid={`kb-cat-${c.category.replace(/\s+/g, "-").toLowerCase()}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition ${
                  activeCategory === c.category
                    ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                    : "hover:bg-white/5 text-zinc-400"
                }`}
              >
                <span className="truncate">{c.category}</span>
                <span className="text-xs text-zinc-500 ml-2">{c.count}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

function ArticleCard({ article }) {
  return (
    <Link
      to={`/knowledge-base/${article.slug}`}
      data-testid={`kb-article-card-${article.slug}`}
      className="group block rounded-2xl border border-white/10 bg-white/5 hover:bg-white/[0.07] hover:border-rose-500/40 transition p-5"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-rose-400 mb-1.5">{article.category}</div>
          <h3 className="font-display font-bold text-lg leading-tight text-white group-hover:text-rose-200">
            {article.title}
          </h3>
          {article.summary && (
            <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{article.summary}</p>
          )}
        </div>
        <ChevronRight size={18} className="text-zinc-500 group-hover:text-rose-400 shrink-0 mt-1" />
      </div>
    </Link>
  );
}

function ArticleView({ slug }) {
  const nav = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/kb/${slug}`)
      .then(async (r) => {
        if (cancelled) return;
        setArticle(r.data);
        try {
          const rel = await api.get("/kb", { params: { category: r.data.category } });
          if (!cancelled) setRelated(rel.data.filter((a) => a.slug !== slug).slice(0, 5));
        } catch (e) { console.warn("Related articles fetch failed", e); }
      })
      .catch((e) => !cancelled && toast.error(errMsg(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-zinc-500">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading article…
      </div>
    );
  }
  if (!article) {
    return (
      <div className="text-center py-24" data-testid="kb-article-not-found">
        <div className="text-zinc-400 mb-4">This article wasn't found.</div>
        <Link to="/knowledge-base" className="text-rose-400 hover:text-rose-300 inline-flex items-center gap-2">
          <ArrowLeft size={16} /> Back to Knowledge Base
        </Link>
      </div>
    );
  }

  return (
    <article className="max-w-3xl" data-testid="kb-article-view">
      <button
        onClick={() => nav("/knowledge-base")}
        className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6"
        data-testid="kb-back-btn"
      >
        <ArrowLeft size={16} /> All articles
      </button>
      <div className="text-[11px] uppercase tracking-wider text-rose-400 mb-2">{article.category}</div>
      <h1 className="font-display font-black text-4xl md:text-5xl leading-tight mb-4" data-testid="kb-article-title">
        {article.title}
      </h1>
      {article.summary && <p className="text-lg text-zinc-300 mb-8">{article.summary}</p>}

      {article.content && (
        <PipeText text={article.content} className="prose prose-invert max-w-none text-zinc-200 leading-relaxed text-[15px]" />
      )}

      {Array.isArray(article.steps) && article.steps.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display font-bold text-xl mb-4">Step-by-step</h2>
          <ol className="space-y-3" data-testid="kb-article-steps">
            {article.steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-rose-500/20 text-rose-300 text-sm font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="text-zinc-200 leading-relaxed pt-0.5">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {Array.isArray(article.common_questions) && article.common_questions.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display font-bold text-xl mb-4">People also ask</h2>
          <ul className="space-y-2" data-testid="kb-article-questions">
            {article.common_questions.map((q, i) => (
              <li key={i}>
                <button
                  onClick={() => openSandy(q)}
                  className="text-left w-full text-sm text-zinc-300 hover:text-rose-300 flex items-start gap-2"
                >
                  <Hash size={14} className="mt-0.5 text-rose-400 shrink-0" /> {q}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(article.related_page || article.related_dashboard) && (
        <div className="mt-10 flex flex-wrap gap-2">
          {article.related_page && (
            <Link
              to={article.related_page}
              className="inline-flex items-center gap-2 text-xs rounded-full border border-white/15 px-3 py-1.5 text-zinc-300 hover:text-white hover:border-rose-500/50"
            >
              <ExternalLink size={12} /> {article.related_page}
            </Link>
          )}
          {article.related_dashboard && (
            <Link
              to={article.related_dashboard}
              className="inline-flex items-center gap-2 text-xs rounded-full border border-white/15 px-3 py-1.5 text-zinc-300 hover:text-white hover:border-rose-500/50"
            >
              <ExternalLink size={12} /> {article.related_dashboard}
            </Link>
          )}
        </div>
      )}

      {article.updated_at && (
        <div className="mt-8 flex items-center gap-2 text-xs text-zinc-500">
          <Clock size={12} /> Last updated {String(article.updated_at).slice(0, 10)}
        </div>
      )}

      <div className="mt-12 rounded-2xl border border-rose-500/30 bg-gradient-to-br from-rose-500/10 to-orange-500/5 p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div>
          <div className="font-display font-bold text-lg mb-1">Still need help?</div>
          <div className="text-sm text-zinc-300">Ask Sandy — she can answer questions and even take actions for you.</div>
        </div>
        <AskSandyCTA prompt={`I read the article "${article.title}" and still have a question.`} label="Ask Sandy" />
      </div>

      {related.length > 0 && (
        <div className="mt-14">
          <h2 className="font-display font-bold text-xl mb-4">More in {article.category}</h2>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="kb-related-list">
            {related.map((a) => <ArticleCard key={a.slug} article={a} />)}
          </div>
        </div>
      )}
    </article>
  );
}

export default function KnowledgeBase() {
  const { slug } = useParams();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [q, setQ] = useState("");
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    api.get("/kb/categories")
      .then((r) => setCategories(r.data || []))
      .catch(() => setCategories([]))
      .finally(() => setLoadingCats(false));
  }, []);

  const loadList = useCallback(() => {
    setLoadingList(true);
    const params = {};
    if (activeCategory) params.category = activeCategory;
    if (q.trim()) params.q = q.trim();
    api.get("/kb", { params })
      .then((r) => setArticles(r.data || []))
      .catch(() => setArticles([]))
      .finally(() => setLoadingList(false));
  }, [activeCategory, q]);

  useEffect(() => {
    if (slug) return;
    const t = setTimeout(loadList, 200);
    return () => clearTimeout(t);
  }, [loadList, slug]);

  const grouped = useMemo(() => {
    if (activeCategory) return null;
    const g = {};
    for (const a of articles) {
      const k = a.category || "General";
      (g[k] = g[k] || []).push(a);
    }
    return g;
  }, [articles, activeCategory]);

  if (slug) {
    return (
      <div className="min-h-screen px-6 md:px-12 lg:px-20 py-16 max-w-6xl mx-auto">
        <ArticleView slug={slug} />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-20 pt-16 pb-10 border-b border-white/5">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-rose-400 mb-4">
            <BookOpen size={14} /> Knowledge Base
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4">
            How can we help?
          </h1>
          <p className="text-zinc-300 text-lg max-w-2xl">
            Guides, how-tos and answers for events, venues, artists, deals, awards and everything else on Gulf Coast VIP.
          </p>
          <div className="mt-8 max-w-2xl">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles… e.g. 'RSVP', 'boost event', 'claim venue'"
                data-testid="kb-search-input"
                className="w-full pl-11 pr-4 py-3.5 rounded-full bg-zinc-900/80 border border-white/10 focus:border-rose-500/60 focus:outline-none text-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="px-6 md:px-12 lg:px-20 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
          <Sidebar
            categories={categories}
            activeCategory={activeCategory}
            onSelect={setActiveCategory}
            loading={loadingCats}
          />

          <div className="min-w-0">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display font-bold text-2xl" data-testid="kb-list-heading">
                  {activeCategory || (q.trim() ? `Results for "${q.trim()}"` : "Browse all articles")}
                </h2>
                {!loadingList && (
                  <div className="text-xs text-zinc-500 mt-1">
                    {articles.length} article{articles.length !== 1 ? "s" : ""}
                  </div>
                )}
              </div>
              <AskSandyCTA />
            </div>

            {loadingList ? (
              <div className="flex items-center justify-center py-24 text-zinc-500">
                <Loader2 className="animate-spin mr-2" size={18} /> Loading articles…
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-16 rounded-2xl border border-dashed border-white/10" data-testid="kb-empty-state">
                <Sparkles className="mx-auto text-zinc-600 mb-3" size={28} />
                <div className="text-zinc-300 font-medium mb-1">No articles matched</div>
                <div className="text-sm text-zinc-500 mb-5">Try a different keyword — or just ask Sandy.</div>
                <AskSandyCTA prompt={q.trim() || undefined} label="Ask Sandy instead" />
              </div>
            ) : activeCategory || q.trim() ? (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="kb-article-grid">
                {articles.map((a) => <ArticleCard key={a.slug} article={a} />)}
              </div>
            ) : (
              <div className="space-y-10">
                {Object.entries(grouped || {}).map(([cat, list]) => (
                  <div key={cat} data-testid={`kb-group-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-display font-bold text-lg text-white">{cat}</h3>
                      <button
                        onClick={() => setActiveCategory(cat)}
                        className="text-xs text-rose-400 hover:text-rose-300"
                      >
                        View all →
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {list.slice(0, 4).map((a) => <ArticleCard key={a.slug} article={a} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
